import { useState } from 'react';

import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAuth } from '@/contexts/auth';
import { userService } from '@/services/user';
import { useToast } from '@/hooks/use-toast';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const userInfoSchema = z.object({
  email: z.string(),
  fullName: z.string().min(1, 'Nome completo é obrigatório'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
  newPassword: z.string().min(8, 'Nova senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Senhas não coincidem',
  path: ['confirmPassword'],
});

type UserInfoFormData = z.infer<typeof userInfoSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function Account() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const userInfoForm = useForm<UserInfoFormData>({
    mode: 'onChange',
    resolver: zodResolver(userInfoSchema),
    defaultValues: {
      email: user?.email || '',
      fullName: user?.name || '',
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    mode: 'onChange',
    resolver: zodResolver(passwordSchema),
  });

  const handleUpdateUserInfo = async (data: UserInfoFormData) => {
    try {
      setIsUpdatingInfo(true);
      await userService.updateUser(user?.id, {
        name: data.fullName,
      });
      await refreshUser();
      toast({
        title: 'Dados atualizados',
        variant: 'success',
        description: 'Informações atualizadas com sucesso!',
      });
    } catch (error) {
      console.error('Error updating user info:', error);
      toast({
        title: 'Algo deu errado',
        description: 'Erro ao atualizar informações. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUpdatingInfo(false);
    }
  };

  const handleUpdatePassword = async (data: PasswordFormData) => {
    try {
      setIsUpdatingPassword(true);
      await userService.updateUser(user?.id, {
        newPassword: data.newPassword,
        oldPassword: data.currentPassword,
      });
      await refreshUser();
      passwordForm.reset();
      toast({
        title: 'Dados atualizados',
        variant: 'success',
        description: 'Senha atualizada com sucesso!',
      });
    } catch (error) {
      console.error('Error updating password:', error);
      toast({
        title: 'Algo deu errado',
        description: 'Erro ao atualizar senha. Verifique a senha atual e tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Informações do Usuário</h2>
          <p className="text-muted-foreground">Visualize e atualize seus dados pessoais e informações de conta.</p>
        </div>
        <div>
          <form onSubmit={userInfoForm.handleSubmit(handleUpdateUserInfo)} className="space-y-4">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                  E-mail
                </label>
                <Input
                  id="email"
                  type="email"
                  {...userInfoForm.register('email')}
                  className="w-full"
                  disabled
                />
              </div>
              <div>
                <label htmlFor="fullName" className="block mb-2 text-sm font-medium text-gray-700">
                  Nome Completo
                </label>
                <Input
                  id="fullName"
                  type="text"
                  {...userInfoForm.register('fullName')}
                  error={userInfoForm.formState.errors.fullName?.message}
                  className="w-full"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={isUpdatingInfo || !userInfoForm.formState.isValid}
              className="bg-black text-white hover:bg-gray-900 "
            >
              {isUpdatingInfo ? 'Salvando...' : 'Salvar'}
            </Button>
          </form>
        </div>
      </div>
      <hr className="border-gray-200" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Mude sua senha</h2>
          <p className="text-muted-foreground">Atualize sua senha para manter sua conta segura.</p>
        </div>
        <div>
          <form onSubmit={passwordForm.handleSubmit(handleUpdatePassword)} className="space-y-4">
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Senha atual
                  </label>
                  <Link to="/forgot-password" className="underline text-sm text-muted-foreground">
                    Esqueceu senha?
                  </Link>
                </div>
                <Input
                  id="currentPassword"
                  type="password"
                  {...passwordForm.register('currentPassword')}
                  className="w-full"
                  error={passwordForm.formState.errors.currentPassword?.message}
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block mb-2 text-sm font-medium text-gray-700">
                  Nova senha
                </label>
                <Input
                  id="newPassword"
                  type="password"
                  {...passwordForm.register('newPassword')}
                  className="w-full"
                  error={passwordForm.formState.errors.newPassword?.message}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-700">
                  Confirme nova senha
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  {...passwordForm.register('confirmPassword')}
                  className="w-full"
                  error={passwordForm.formState.errors.confirmPassword?.message}
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={isUpdatingPassword || !passwordForm.formState.isValid}
              className="bg-black text-white hover:bg-gray-900"
            >
              {isUpdatingPassword ? 'Salvando...' : 'Salvar'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}